/**
 * Sales documents a payment institution checks before opening a virtual POS:
 * distance sales agreement, pre-information form, cancellation and refund
 * policy, plus the seller details every one of them repeats.
 *
 * Kept in the frontend (not fetched like /terms-of-service) so the pages are
 * prerendered: a reviewer, or a crawler, reads the full text without the API.
 *
 * Turkish is the binding text; English is a courtesy translation. Prices come
 * from lib/pricing.ts so a price change cannot leave a contract behind.
 */
import { formatMoney, PLAN_BASE_PRICE } from "../lib/pricing";

export const SELLER = {
  name: "TUBU ARGE A.Ş.",
  brand: "Hümo",
  mersis: "0859065580900001",
  taxOffice: "Pendik Vergi Dairesi",
  taxOfficeEn: "Pendik Tax Office",
  taxId: "8590655809",
  address: "Teknopark İstanbul, 1/4C No:307, Pendik/İstanbul",
  phone: "0850 840 1859",
  email: "support@meethumo.com",
  website: "https://meethumo.com",
} as const;

/** Last change to any of the three documents below. */
export const SALES_TERMS_UPDATED = "2026-10-16";

export type SalesDocId = "distance-sales" | "pre-information" | "refund";

export interface SalesDocSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
}

export interface SalesDoc {
  title: string;
  intro: string;
  sections: SalesDocSection[];
}

export const SALES_DOC_PATHS: Record<SalesDocId, string> = {
  "distance-sales": "/distance-sales-agreement",
  "pre-information": "/pre-information-form",
  refund: "/refund-policy",
};

const tl = (tier: "STARTER" | "PRO" | "GROWTH") => formatMoney(PLAN_BASE_PRICE[tier]!.TRY, "TRY");

const sellerItemsTr = [
  `Unvan: ${SELLER.name} (${SELLER.brand} markasının sahibi)`,
  `Adres: ${SELLER.address}`,
  `MERSİS No: ${SELLER.mersis}`,
  `Vergi Dairesi / No: ${SELLER.taxOffice} / ${SELLER.taxId}`,
  `Telefon: ${SELLER.phone}`,
  `E-posta: ${SELLER.email}`,
  `Web sitesi: ${SELLER.website}`,
];

const sellerItemsEn = [
  `Company: ${SELLER.name} (owner of the ${SELLER.brand} brand)`,
  `Address: ${SELLER.address}, Türkiye`,
  `MERSIS No: ${SELLER.mersis}`,
  `Tax office / ID: ${SELLER.taxOfficeEn} / ${SELLER.taxId}`,
  `Phone: +90 ${SELLER.phone.slice(1)}`,
  `Email: ${SELLER.email}`,
  `Website: ${SELLER.website}`,
];

const plansTr = [
  `Free: ücretsiz, süresiz`,
  `Starter: ${tl("STARTER")} / ay`,
  `Growth: ${tl("PRO")} / ay`,
  `Scale: ${tl("GROWTH")} / ay`,
  `Enterprise: yazılı teklif ile belirlenir`,
];

const plansEn = [
  `Free: no charge, no time limit`,
  `Starter: ${tl("STARTER")} per month`,
  `Growth: ${tl("PRO")} per month`,
  `Scale: ${tl("GROWTH")} per month`,
  `Enterprise: set by written quote`,
];

const TR: Record<SalesDocId, SalesDoc> = {
  "distance-sales": {
    title: "Mesafeli Hizmet Sözleşmesi",
    intro:
      "Bu sözleşme, Hümo web sitesi veya uygulaması üzerinden ücretli bir plan satın alan ALICI ile HİZMET SAĞLAYICI arasında, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca elektronik ortamda kurulur.",
    sections: [
      { heading: "1. Hizmet Sağlayıcı", items: sellerItemsTr },
      {
        heading: "2. Alıcı",
        paragraphs: [
          "Hümo hesabını açan ve ödeme ekranında sipariş veren gerçek veya tüzel kişidir. Alıcının adı, e-posta adresi ve fatura bilgileri hesap ve ödeme sırasında verdiği bilgilerdir.",
        ],
      },
      {
        heading: "3. Sözleşmenin Konusu",
        paragraphs: [
          "Alıcının seçtiği Hümo planı kapsamında; WhatsApp, Instagram, e-posta ve web sitesi gibi kanallardan gelen müşteri mesajlarını tek gelen kutusunda toplayan, yapay zekâ ile yanıt öneren ve gönderen, satış analizi sunan bulut tabanlı yazılım hizmetinin (SaaS) sunulmasıdır. Hizmet fiziksel bir ürün içermez; kargo veya teslimat yoktur.",
        ],
      },
      {
        heading: "4. Bedel ve Ödeme",
        items: [
          ...plansTr,
          "Fiyatlar Türk lirasıdır ve KDV dahildir.",
          "İlk ödeme, kredi veya banka kartıyla, Paratika Ödeme Hizmetleri A.Ş. altyapısında 3D Secure doğrulamasıyla tek çekim olarak alınır.",
          "Abonelik aylıktır ve alıcı iptal edene kadar her ay, ilk ödeme gününe denk gelen tarihte aynı karttan otomatik olarak yenilenir. Bunun için kart bilgileri Paratika tarafından güvenli şekilde saklanır; Hümo kart numarasını görmez ve saklamaz.",
          "Fiyat değişiklikleri, yürürlüğe girmeden en az 30 gün önce e-postayla bildirilir. Yeni fiyatı kabul etmeyen alıcı, değişiklikten önce aboneliğini iptal edebilir.",
          "Yenileme ödemesi alınamazsa alıcı e-postayla bilgilendirilir; ödeme yapılmazsa plan, ödenen dönemin sonunda ücretsiz plana geçer.",
          "Planın içerdiği kullanım miktarını aşan AI aksiyonu veya sonuç bazlı ücretler fiyatlar sayfasında yazılıdır. Bu ücretler alıcıya önceden bildirilmeden ve onayı alınmadan tahsil edilmez.",
          "Fatura, ödemeden sonra alıcının verdiği bilgilerle elektronik olarak düzenlenir.",
        ],
      },
      {
        heading: "5. Hizmetin İfası",
        paragraphs: [
          "Ödeme onaylandığı anda satın alınan plan alıcının hesabında açılır ve abonelik iptal edilene kadar kullanılabilir. Ödeme onaylanmazsa plan değişmez ve karttan tutar çekilmez.",
        ],
      },
      {
        heading: "6. Cayma Hakkı ve İade",
        paragraphs: [
          "Alıcı, ilk ödeme tarihinden itibaren 14 gün içinde hiçbir gerekçe göstermeden ve ceza ödemeden sözleşmeden cayabilir. Hizmetin bu süre içinde kullanılmış olması cayma hakkını ortadan kaldırmaz.",
          `Cayma bildirimi ${SELLER.email} adresine e-postayla veya ${SELLER.phone} numaralı telefondan yapılır. Hümo, bildirimin ulaşmasından itibaren en geç 14 gün içinde ödenen tutarın tamamını ödemenin yapıldığı karta iade eder. İadenin karta yansıma süresi kartı çıkaran bankaya bağlıdır.`,
          "14 günlük süre geçtikten sonra yapılan iptallerde ve yenileme ödemelerinde, içinde bulunulan ayın ücreti iade edilmez; plan o ayın sonuna kadar kullanılabilir ve sonraki yenileme yapılmaz.",
        ],
      },
      {
        heading: "7. İptal",
        paragraphs: [
          `Alıcı aboneliğini istediği zaman, uygulamadaki Plan sayfasından veya ${SELLER.email} adresine e-posta göndererek iptal edebilir. İptal, ödenmiş olan ayın sonunda geçerli olur; o tarihten sonra karttan çekim yapılmaz ve hesap ücretsiz plana geçer. İptal, alıcıya e-postayla teyit edilir.`,
          "Hesaptaki konuşmalar ve ayarlar iptalle silinmez; hesap silme talepleri Gizlilik Politikası'ndaki yolla yapılır.",
        ],
      },
      {
        heading: "8. Tarafların Yükümlülükleri",
        items: [
          "Hümo, hizmeti makul özen ve süreklilikle sunar; planlı bakımları önceden duyurur.",
          "Alıcı, hesabını hukuka ve bağlı kanalların (Meta, WhatsApp vb.) kurallarına uygun kullanır; hesap bilgilerinin güvenliğinden sorumludur.",
          "Kişisel verilerin işlenmesi Gizlilik Politikası ve KVKK Aydınlatma Metni'ne tabidir.",
        ],
      },
      {
        heading: "9. Uyuşmazlıklar",
        paragraphs: [
          "Tüketici alıcılar, Ticaret Bakanlığı'nın her yıl ilan ettiği parasal sınırlar içinde yerleşim yerlerindeki veya işlemin yapıldığı yerdeki Tüketici Hakem Heyeti'ne, bu sınırların üzerinde Tüketici Mahkemesi'ne başvurabilir. Tacir alıcılarla doğan uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.",
        ],
      },
      {
        heading: "10. Yürürlük",
        paragraphs: [
          "Alıcı, ödeme ekranında Ön Bilgilendirme Formu'nu ve bu sözleşmeyi okuyup onayladığında sözleşme kurulmuş olur. Sözleşmenin güncel metni bu sayfada her zaman erişilebilir durumdadır.",
        ],
      },
    ],
  },
  "pre-information": {
    title: "Ön Bilgilendirme Formu",
    intro:
      "Mesafeli Sözleşmeler Yönetmeliği'nin 5. maddesi uyarınca, ödeme yapmadan önce bilmeniz gerekenler aşağıdadır.",
    sections: [
      { heading: "1. Hizmet Sağlayıcı", items: sellerItemsTr },
      {
        heading: "2. Hizmetin Temel Nitelikleri",
        paragraphs: [
          "Hümo; WhatsApp, Instagram, e-posta ve web sitesi sohbetinden gelen müşteri mesajlarını tek gelen kutusunda toplayan, yapay zekâ ile yanıtlayan, satış temsilcisine öneri sunan bulut tabanlı bir yazılım hizmetidir. Planların içerdiği konuşma, AI aksiyonu ve kanal sayıları fiyatlar sayfasında yazılıdır.",
        ],
      },
      { heading: "3. Fiyatlar (KDV dahil, Türk lirası)", items: plansTr },
      {
        heading: "4. Ödeme",
        paragraphs: [
          "Kredi veya banka kartıyla, Paratika altyapısında 3D Secure doğrulamasıyla tek çekim. Abonelik aylıktır; iptal edene kadar her ay aynı karttan otomatik yenilenir. İstediğiniz zaman Plan sayfasından iptal edebilirsiniz; iptal, ödenmiş ayın sonunda geçerli olur. Kargo, teslimat veya ek masraf yoktur.",
        ],
      },
      {
        heading: "5. İfa",
        paragraphs: ["Plan, ödeme onaylandığı anda hesabınızda açılır."],
      },
      {
        heading: "6. Cayma Hakkı",
        paragraphs: [
          `İlk ödeme tarihinden itibaren 14 gün içinde gerekçe göstermeden cayabilir ve ödediğiniz tutarın tamamını geri alabilirsiniz. Bildirim: ${SELLER.email} veya ${SELLER.phone}. İade, bildirimden itibaren en geç 14 gün içinde ödemenin yapıldığı karta yapılır.`,
        ],
      },
      {
        heading: "7. Şikâyet ve Başvuru",
        paragraphs: [
          `Şikâyetlerinizi ${SELLER.email} adresine iletebilirsiniz. Tüketiciler, parasal sınırlar dahilinde Tüketici Hakem Heyeti'ne, üzerinde Tüketici Mahkemesi'ne başvurabilir.`,
        ],
      },
    ],
  },
  refund: {
    title: "İptal ve İade Koşulları",
    intro: "Kısaca: ilk ödemeden sonraki 14 gün koşulsuz tam iade; abonelik aylık yenilenir ve istediğiniz zaman iptal edebilirsiniz.",
    sections: [
      {
        heading: "14 gün içinde tam iade",
        paragraphs: [
          "İlk ödemenizden sonraki 14 gün içinde, gerekçe göstermeden iade isteyebilirsiniz. Hizmeti bu süre içinde kullanmış olmanız iadeye engel değildir. Ödediğiniz tutarın tamamı, ödemeyi yaptığınız karta iade edilir.",
        ],
      },
      {
        heading: "Nasıl iade isterim?",
        items: [
          `${SELLER.email} adresine, hesabınızın e-posta adresinden "İade talebi" konulu bir e-posta gönderin veya ${SELLER.phone} numarasını arayın.`,
          "Talebinizi en geç 14 gün içinde işleme alırız. Tutarın kartınıza yansıma süresi bankanıza bağlıdır; genellikle 2–10 iş günüdür.",
          "İade onaylandığında abonelik sona erer ve plan hemen ücretsiz plana döner.",
        ],
      },
      {
        heading: "14 günden sonra ve yenilemelerde",
        paragraphs: [
          "Aylık yenileme ödemeleri ve 14 günden sonra yapılan iptaller için içinde bulunulan ayın ücreti iade edilmez. Planınız o ayın sonuna kadar açık kalır, sonraki ay çekim yapılmaz.",
        ],
      },
      {
        heading: "Otomatik yenileme ve iptal",
        items: [
          "Aboneliğiniz iptal edene kadar her ay, ilk ödeme gününe denk gelen tarihte aynı karttan otomatik yenilenir.",
          `İptal etmek için uygulamadaki Plan sayfasını kullanın veya ${SELLER.email} adresine yazın. İptal ödenmiş ayın sonunda geçerli olur ve size e-postayla teyit edilir.`,
          "Yenileme ödemesi alınamazsa sizi e-postayla bilgilendiririz; ödeme yapılmazsa plan ay sonunda ücretsiz plana geçer.",
          "İptalde konuşmalarınız ve ayarlarınız silinmez.",
        ],
      },
      {
        heading: "Hatalı veya mükerrer çekim",
        paragraphs: [
          "Aynı dönem için iki kez ödeme alındıysa veya hatalı bir tutar çekildiyse, süreye bakılmaksızın fazla tutarı iade ederiz.",
        ],
      },
      { heading: "Hizmet sağlayıcı", items: sellerItemsTr },
    ],
  },
};

const EN: Record<SalesDocId, SalesDoc> = {
  "distance-sales": {
    title: "Distance Sales Agreement",
    intro:
      "This agreement is formed electronically between the BUYER who purchases a paid Hümo plan and the SERVICE PROVIDER, under Turkish Consumer Protection Law No. 6502 and the Distance Contracts Regulation. The Turkish text is binding; this is a translation.",
    sections: [
      { heading: "1. Service provider", items: sellerItemsEn },
      {
        heading: "2. Buyer",
        paragraphs: ["The person or company that opens a Hümo account and places the order on the payment screen, with the details given there."],
      },
      {
        heading: "3. Subject",
        paragraphs: [
          "Provision of the cloud software service (SaaS) in the chosen Hümo plan: a shared inbox for WhatsApp, Instagram, email and website messages, AI replies and suggestions, and sales analytics. No physical goods, no shipping.",
        ],
      },
      {
        heading: "4. Price and payment",
        items: [
          ...plansEn,
          "Prices are in Turkish lira and include VAT.",
          "The first payment is made by credit or debit card with 3D Secure, through Paratika Ödeme Hizmetleri A.Ş.",
          "The subscription is monthly and renews automatically from the same card every month until you cancel. Paratika stores the card for this; Hümo never sees or stores card details.",
          "Price changes are announced by email at least 30 days ahead; you may cancel before they apply.",
          "Usage over a plan's included amount is priced on the pricing page and is never charged without notice and your approval.",
        ],
      },
      { heading: "5. Delivery", paragraphs: ["The plan is active on your account as soon as the payment is approved, until you cancel."] },
      {
        heading: "6. Right of withdrawal and refunds",
        paragraphs: [
          `You may withdraw within 14 days of the first payment without giving a reason, even if you have used the service, by writing to ${SELLER.email}. We refund the full amount to the card used within 14 days of your notice.`,
          "Renewals, and cancellations after 14 days, are not refunded for the current month; the plan stays active until the month ends and is not renewed.",
        ],
      },
      {
        heading: "7. Cancellation",
        paragraphs: [`Cancel any time from the Plan page in the app or by emailing ${SELLER.email}. Cancellation takes effect at the end of the paid month, is confirmed by email, and the account then moves to the Free plan with its data kept.`],
      },
      {
        heading: "8. Disputes",
        paragraphs: [
          "Consumers may apply to the Consumer Arbitration Committee within the annual monetary limits, or to the Consumer Courts above them. For business buyers, the courts and enforcement offices of Istanbul Anadolu have jurisdiction.",
        ],
      },
    ],
  },
  "pre-information": {
    title: "Pre-Information Form",
    intro: "What you need to know before paying, as required by Article 5 of the Turkish Distance Contracts Regulation.",
    sections: [
      { heading: "1. Service provider", items: sellerItemsEn },
      {
        heading: "2. The service",
        paragraphs: ["Hümo is a cloud software service that gathers customer messages into one inbox and answers them with AI. Each plan's limits are listed on the pricing page."],
      },
      { heading: "3. Prices (VAT included, Turkish lira)", items: plansEn },
      {
        heading: "4. Payment",
        paragraphs: ["Card payment with 3D Secure through Paratika. Monthly subscription that renews automatically from the same card until you cancel from the Plan page; cancellation applies at the end of the paid month. No shipping, no extra fees."],
      },
      {
        heading: "5. Right of withdrawal",
        paragraphs: [`Full refund within 14 days of the first payment, no reason needed: ${SELLER.email}.`],
      },
    ],
  },
  refund: {
    title: "Cancellation and Refund Policy",
    intro: "In short: a full refund within 14 days of your first payment; the subscription renews monthly and you can cancel any time.",
    sections: [
      {
        heading: "Full refund within 14 days",
        paragraphs: ["Ask within 14 days of your first payment and we refund the full amount to your card, whether or not you used the service."],
      },
      {
        heading: "How to ask",
        items: [
          `Email ${SELLER.email} from your account's address with the subject "Refund request".`,
          "We process it within 14 days; your bank usually shows it within 2–10 business days.",
        ],
      },
      { heading: "Renewals and after 14 days", paragraphs: ["The current month is not refunded; the plan stays active until it ends and is not renewed."] },
      {
        heading: "Automatic renewal and cancellation",
        paragraphs: [`The subscription renews from the same card every month until you cancel from the Plan page or by emailing ${SELLER.email}. Cancellation applies at the end of the paid month and is confirmed by email; your data is kept.`],
      },
      { heading: "Duplicate or wrong charges", paragraphs: ["Refunded in full, whenever you notice them."] },
      { heading: "Service provider", items: sellerItemsEn },
    ],
  },
};

export function getSalesDoc(id: SalesDocId, language: string): SalesDoc {
  return (language === "tr" ? TR : EN)[id];
}
